import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { RotateCcw } from "lucide-react";
import LandingNav from "../components/landing/LandingNav";
import Footer from "../components/landing/Footer";
import { useAuth } from "../context/AuthContext";

const STEPS = [
  { num: "01", title: "Identifica", text: "Cada máquina tiene su propia identidad digital.", testid: "demo-step-1" },
  { num: "02", title: "Consulta", text: "Documentación, fotografías e información desde el lugar donde está la máquina.", testid: "demo-step-2" },
  { num: "03", title: "Recuerda", text: "Mantenimientos, averías, reparaciones y conocimiento permanecen asociados al activo.", testid: "demo-step-3" },
  { num: "04", title: "Construye su historia", text: "Cada nueva intervención hace más útil el pasaporte.", testid: "demo-step-4" },
];

export default function Demo() {
  const { user } = useAuth();
  const ctaTo = user ? "/app/machines/new" : "/solicitar-acceso";
  const videoRef = useRef(null);
  const [ended, setEnded] = useState(false);

  const replay = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="demo-page">
      <LandingNav />
      <main>
        <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pt-16 sm:pt-24 pb-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="max-w-3xl mx-auto text-center"
          >
            <p className="text-xs uppercase tracking-widest font-medium text-zinc-500 mb-5">Demo</p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-950 leading-[1.08]">
              Mira cómo funciona Asset Passport.
            </h1>
            <p className="text-lg sm:text-xl text-zinc-600 leading-relaxed mt-6 max-w-2xl mx-auto">
              Una máquina. Un QR. Toda su información e historial en un único lugar.
            </p>
          </motion.div>
        </section>

        <section className="max-w-5xl mx-auto px-6 sm:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.15 }}
            className="relative aspect-video rounded-3xl overflow-hidden bg-zinc-950 shadow-[0_24px_70px_-20px_rgba(0,0,0,0.35)]"
          >
            <video
              ref={videoRef}
              className="absolute inset-0 h-full w-full object-cover"
              preload="none"
              poster="/demo/poster.jpg"
              controls
              playsInline
              onEnded={() => setEnded(true)}
              onPlay={() => setEnded(false)}
              data-testid="demo-video"
            >
              <source src="/demo/demo_v2.mp4" type="video/mp4" />
            </video>
            {ended && (
              <div
                className="absolute inset-0 bg-zinc-950/90 backdrop-blur-sm flex flex-col items-center justify-center text-center px-6 animate-fade-up"
                data-testid="demo-endscreen"
              >
                <p className="font-heading text-2xl sm:text-4xl font-semibold tracking-tight text-white leading-tight max-w-2xl">
                  Dale una identidad digital a tu primera máquina.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3 mt-9">
                  <Link
                    to={ctaTo}
                    data-testid="demo-endscreen-cta"
                    className="inline-flex items-center px-7 py-3.5 rounded-full bg-white text-zinc-900 font-medium hover:scale-[1.03] active:scale-95 transition-transform duration-200"
                  >
                    {user ? "Crear mi primera máquina" : "Probar Asset Passport"}
                  </Link>
                  <button
                    onClick={replay}
                    data-testid="demo-replay-button"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/25 text-white font-medium hover:bg-white/10 active:scale-95 transition-[background-color,transform] duration-200"
                  >
                    <RotateCcw size={16} /> Volver a ver
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </section>

        <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-24">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8" data-testid="demo-steps">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.num}
                data-testid={s.testid}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <span className="font-heading text-5xl font-bold tracking-tight text-zinc-200 leading-none select-none">
                  {s.num}
                </span>
                <h3 className="font-heading text-xl font-semibold tracking-tight text-zinc-900 mt-4">
                  {s.title}
                </h3>
                <p className="text-base text-zinc-600 leading-relaxed mt-2">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="bg-white border-t border-zinc-100">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-24 lg:py-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.65 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 leading-[1.08]">
                Empieza con una máquina.
              </h2>
              <p className="text-lg text-zinc-600 leading-relaxed mt-5">
                Crear el primer pasaporte lleva minutos. El valor empieza desde el primer día.
              </p>
              <div className="mt-10">
                <Link
                  to={ctaTo}
                  data-testid="demo-final-cta"
                  className="inline-flex items-center px-9 py-4 rounded-full bg-zinc-900 text-white text-lg font-medium hover:bg-zinc-800 hover:scale-[1.03] active:scale-95 transition-[background-color,transform] duration-200"
                >
                  {user ? "Crear mi primera máquina" : "Probar Asset Passport"}
                </Link>
              </div>
              <div className="mt-6">
                <Link
                  to="/"
                  data-testid="demo-back-home"
                  className="text-sm font-medium text-zinc-500 hover:text-zinc-900 underline underline-offset-4 transition-colors"
                >
                  Volver a la página principal
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
